import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  HowToPlayCTATitle,
  HowToPlayCTASubtitle,
  HowToPlayCTAButton1,
  HowToPlayCTAGetStarted,
} from '../../../content/keys';

const FALLBACKS = {
  [HowToPlayCTATitle]: 'Ready to Get Started?',
  [HowToPlayCTASubtitle]: 'Join thousands of players and get in on the action today!',
  [HowToPlayCTAButton1]: 'Sign Up Now',
  [HowToPlayCTAGetStarted]: 'Get Started',
};

export default function HowToCtaSection({ section }) {
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { t: tr } = useTranslations();
  const navigate = useNavigate();
  const { isGuestCheckoutEnabled } = useAuthConfig();
  const { user } = useAuth();

  const isGuest = isGuestCheckoutEnabled();
  const isGetStarted = isGuest || !!user;
  const buttonKey = isGetStarted ? HowToPlayCTAGetStarted : HowToPlayCTAButton1;
  const buttonFallback = isGetStarted ? 'Get Started' : 'Sign Up Now';

  // Use tr() only when it returns a real translation, not the key name itself
  const t = (key) => { const v = tr(key); return c(key, (v !== key ? v : null) || FALLBACKS[key] || ''); };

  const handleAction = () => {
    if (editMode) return;
    if (isGetStarted) {
      navigate('/');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div
      className="rounded-2xl p-4 sm:p-8 text-center shadow-xl mb-6 sm:mb-12"
      style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-primary-dark, #1e3a8a))' }}
    >
      <InlineTranslationEdit
        translationKey={HowToPlayCTATitle}
        value={t(HowToPlayCTATitle)}
        fallback={FALLBACKS[HowToPlayCTATitle]}
        editMode={editMode}
        onUpdate={handleTranslationUpdate}
        className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 block"
      />
      <InlineTranslationEdit
        translationKey={HowToPlayCTASubtitle}
        value={t(HowToPlayCTASubtitle)}
        fallback={FALLBACKS[HowToPlayCTASubtitle]}
        editMode={editMode}
        onUpdate={handleTranslationUpdate}
        className="text-xs sm:text-base text-white/80 mb-4 sm:mb-6 max-w-2xl mx-auto block leading-relaxed"
      />
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleAction}
          className="px-8 sm:px-10 py-3 sm:py-3.5 bg-white text-charity-primary font-bold text-sm sm:text-base rounded-xl hover:bg-charity-primary-lightest transition-all hover:scale-105 hover:shadow-lg active:scale-95"
        >
          <InlineTranslationEdit
            translationKey={buttonKey}
            value={c(buttonKey, tr(buttonKey) !== buttonKey ? tr(buttonKey) : buttonFallback)}
            fallback={buttonFallback}
            editMode={editMode}
            onUpdate={handleTranslationUpdate}
          />
        </button>
      </div>
    </div>
  );
}
